import { Overlay } from "@angular/cdk/overlay";
import { inject, Injectable } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { BillingServiceOverviewStore } from "src/app/pages/billing/billing-status-widget/billing-service-overview.store";
import { BillingService } from "src/app/pages/billing/billing.service";
import { ConfirmComponent } from "src/app/shared/components/confirm-generic/confirm.component";
import { withOverlayOnlyScrollStrategy } from "src/app/shared/components/confirm-generic/confirm-dialog.options";
import {
  BILLING_REACTIVATION_ROUTE,
  BillingLockoutSource,
  BillingSubscriptionEndedReason,
} from "./billing-lockout.constants";
import { isBillingReactivationRoute } from "./billing-lockout.helper";

export interface BillingLockoutPromptOptions {
  source: BillingLockoutSource;
  apiMessage?: string | null;
  websocketReason?: BillingSubscriptionEndedReason;
}

@Injectable({
  providedIn: "root",
})
export class BillingSubscriptionLockoutService {
  private readonly dialog = inject(MatDialog);
  private readonly overlay = inject(Overlay);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly billingService = inject(BillingService);
  private readonly overviewStore = inject(BillingServiceOverviewStore);

  private dialogRef: MatDialogRef<ConfirmComponent> | null = null;
  private lastPromptAt = 0;

  /**
   * Clears billing entitlement cache and prompts the user before reactivation.
   * Idempotent while the modal is open or within a short debounce window.
   */
  promptReactivation(options: BillingLockoutPromptOptions): void {
    this.clearEntitlementCache();

    if (isBillingReactivationRoute(this.router.url)) {
      return;
    }

    const now = Date.now();
    if (this.dialogRef || now - this.lastPromptAt < 800) {
      return;
    }
    this.lastPromptAt = now;

    const message = this.resolveMessage(options);

    this.dialogRef = this.dialog.open(
      ConfirmComponent,
      withOverlayOnlyScrollStrategy(this.overlay, {
        disableClose: true,
        autoFocus: "first-tabbable",
        data: {
          title: this.translate.instant("billingLockout.title"),
          message,
          okAction: this.translate.instant("billingLockout.ok"),
          hideHeaderClose: true,
          clearButtonClose: true,
        },
      }),
    );

    this.dialogRef.afterClosed().subscribe((confirmed) => {
      this.dialogRef = null;
      if (confirmed) {
        void this.router.navigateByUrl(BILLING_REACTIVATION_ROUTE);
      }
    });
  }

  private clearEntitlementCache(): void {
    this.billingService.clearCache();
    this.overviewStore.reset();
  }

  private resolveMessage(options: BillingLockoutPromptOptions): string {
    const trimmedApi = options.apiMessage?.trim();
    if (options.source === "http" && trimmedApi) {
      return trimmedApi;
    }

    if (options.source === "websocket") {
      if (options.websocketReason === "billing_suspended") {
        return this.translate.instant("billingLockout.messageBillingSuspended");
      }
      if (options.websocketReason === "subscription_deleted") {
        return this.translate.instant(
          "billingLockout.messageSubscriptionDeleted",
        );
      }
    }

    return this.translate.instant("billingLockout.messageDefault");
  }
}
